import React from "react";
import styled from "styled-components";
import { ReactComponent as GoogleIcon } from "assets/svg/google.svg";
import metaLogo from "assets/images/meta-logo.png";
import { ReactComponent as TiktokIcon } from "assets/svg/tiktok-complex.svg";
import { Platform, PlatformCard } from "./PlatformCard";
import { useFormikContext } from "formik";
import { SetPageFormValues } from "../../types";
import { Section, Title } from "../../Section";
import { useFormChangesContext } from "contexts/FormChangesContext";
import { FlexRow } from "Components/Flex";

type Props = Exclude<React.ComponentProps<typeof Section>, "hasChanges">;

export const TrackingAndAnalyticsSection: React.FC<Props> = ({
  title,
  ...props
}) => {
  const { values, setFieldValue } = useFormikContext<SetPageFormValues>();
  const { compareChanges, changedSections } = useFormChangesContext();

  const setIntegrationsValue = (
    getIntegrations: (
      page: Partial<SetPageFormValues["page"]>,
    ) => Partial<SetPageFormValues["page"]["integrations"]>,
  ) =>
    setFieldValue("page", {
      ...values.page,
      integrations: {
        ...values.page.integrations,
        ...getIntegrations(values.page),
      },
    });

  const platforms: Platform[] = [
    {
      id: "googleTracking",
      logo: <GoogleIcon />,
      name: "Google Analytics",
      description:
        "Connect your Google Analytics tracking code to track your stats with Google Analytics.",

      inputLabel: {
        id: "Google Tag ID (Measurement ID)",
        code: "Google Analytics Tracking Code",
      },
      placeholder: {
        id: "Paste your ID here (example: G-PSW1MY7HB4)",
        code: sampleCode.google,
      },
      extractId: (embed) => {
        // is id attached as param to url?
        let group = embed.match(/id=([A-Za-z0-9-]+)/);
        if (group?.[1]) {
          return group[1];
        }
        // if not in url, look in args
        group = embed.match(/gtag\('config',\s*'([A-Za-z0-9-]+)'/);
        if (group?.[1]) {
          return group[1];
        }

        // if still unable to find, use regex to attract from string
        // see https://stackoverflow.com/questions/2497294/regular-expression-to-validate-a-google-analytics-ua-number/68679610#68679610
        group = embed.match(/[A-Z][A-Z0-9]?-[A-Z0-9]{4,10}(?:-[1-9]\d{0,3})?/);
        return group?.[0];
      },
      value: values.page.integrations?.analytics?.google_tag_manager || "",
      setValue: (value, type) => {
        // we don't know the type on removal so clear both for google analytics/tag manager
        if (type === "remove") {
          setIntegrationsValue((page) => ({
            analytics: {
              ...page.integrations?.analytics,
              google_tag_manager: "",
              google_analytics: "",
            },
          }));
        }
        if (type === "id") {
          setIntegrationsValue((page) => ({
            analytics: {
              ...page.integrations?.analytics,
              google_tag_manager: value,
              google_analytics: "",
            },
          }));
          compareChanges(
            "page.integrations.analytics.google_tag_manager",
            value,
          );
        }
        if (type === "code") {
          setIntegrationsValue((page) => ({
            analytics: {
              ...page.integrations?.analytics,
              google_analytics: value,
              google_tag_manager: "",
            },
          }));
          compareChanges("page.integrations.analytics.google_analytics", value);
        }
      },
    },
    {
      id: "metaTracking",
      logo: <img src={metaLogo} alt="Meta logo" width={60} />,
      name: "Meta Tracking",
      description:
        "Connect your Meta account to run pixel conversion tracking.",
      extractId: (embed) => {
        const group = embed.match(/fbq\('init',\s*'([A-Za-z0-9]+)'\)/);
        return group?.[1];
      },
      inputLabel: { id: "Meta Pixel ID", code: "Meta Dataset Code" },
      placeholder: {
        id: "Paste your ID here (example: A1AAA1AA1AAA1AAAAA1AA)",
        code: sampleCode.meta,
      },
      value: values.page.integrations?.pixelTracking?.meta || "",
      setValue: (value) => {
        setIntegrationsValue((page) => ({
          pixelTracking: {
            ...page.integrations?.pixelTracking,
            meta: value,
          },
        }));
        compareChanges("page.integrations.pixelTracking.meta", value);
      },
    },
    {
      id: "tiktokTracking",
      logo: <TiktokIcon />,
      name: "TikTok Tracking Pixel",
      description:
        "Add your TikTok Pixel code here to create an audience to remarket to.",
      extractId: (embed) => {
        const group = embed.match(/ttq.load\('([A-Za-z0-9]+)'\)/);
        return group?.[1];
      },
      inputLabel: { id: "TikTok Pixel ID", code: "TikTok Pixel Code" },
      placeholder: {
        id: "Paste your ID here (example: 1234567890)",
        code: sampleCode.tiktok,
      },
      value: values.page.integrations?.pixelTracking?.tiktok || "",
      setValue: (value) => {
        setIntegrationsValue((page) => ({
          pixelTracking: {
            ...page.integrations?.pixelTracking,
            tiktok: value,
          },
        }));
        compareChanges("page.integrations.pixelTracking.tiktok", value);
      },
    },
  ];

  const titleWithIcons = (
    <HeaderContainer>
      <FlexRow>
        <Title>{title}</Title>
      </FlexRow>
      <HeaderIcons>
        {platforms
          .filter((platform) => platform.value)
          .map((platform) => (
            <IconContainer key={`${platform.name}-logo`}>
              {platform.logo}
            </IconContainer>
          ))}
      </HeaderIcons>
    </HeaderContainer>
  );
  return (
    <Section
      {...props}
      title={titleWithIcons}
      hasChanges={changedSections.tracking}
    >
      <Container>
        {platforms.map((platform) => (
          <PlatformCard key={`${platform.name}-card`} platform={platform} />
        ))}
      </Container>
    </Section>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 10px;
  position: relative;

  ${({ theme }) => theme.media.mobile} {
    margin-top: 5px;
  }
`;

const HeaderIcons = styled.div`
  display: flex;
  margin-right: 10px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -8px;
  width: 28px;
  height: 28px;
  object-fit: contain;
  border: 1px solid var(--border-color);
  border-radius: 50%;
  padding: 3px;
  background-color: var(--component-background-color);
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const sampleCode = {
  google: `Paste your code here (example below):

<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'G-PSW1MY7HB4');
</script>
  `,
  meta: `Paste your code here (example below):

<!-- Facebook Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)   {if(f.fbq)return;n=f.fbq=function(){n.callMethod?   n.callMethod.apply(n,arguments):n.queue.push(arguments)};   if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';   n.queue=[];t=b.createElement(e);t.async=!0;   t.src=v;s=b.getElementsByTagName(e)[0];   s.parentNode.insertBefore(t,s)}(window, document,'script',   'https://connect.facebook.net/en_US/fbevents.js');   fbq('init', '{your-pixel-id-goes-here}');   fbq('track', 'PageView');
</script>
<noscript>   <img height="1" width="1" style="display:none"         src="https://www.facebook.com/tr?id={your-pixel-id-goes-here}&ev=PageView&noscript=1"/></noscript>
<!-- End Facebook Pixel Code -->
  `,
  tiktok: `Paste your code here (example below):

<script>
!function (w, d, t) {
  w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};  ttq.load('XXXXXXXXXXXXXXXXXXXX');  ttq.page();}(window, document, 'ttq');
</script>
  `,
};
